import {
  anyContainingPermission,
  useAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { AtkNav } from "../AtkNav";
import { Autotractor } from "protos/atk/autotractor";
import { buildPermission, getCustomerId } from "portal/utils/auth";
import { Button, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { CustomerResponse } from "protos/portal/customers";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { DateTime } from "luxon";
import { Farm, Zone } from "protos/farm/farm";
import { farmApi } from "common/state/portalApi/farm";
import { getJobPath, Path } from "portal/utils/routing";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridInitialState as GridInitialStatePremium,
  GridRenderCellParams,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import { Header } from "portal/components/header/Header";
import { Job, State, stateFromJSON } from "protos/rtc/jobs";
import { JobFilters } from "./JobFilters";
import { JobStateChip } from "portal/components/atk/JobStateChip";
import { jobTypeFromJSON } from "protos/atk/job_type";
import { jobTypeToLocaleString } from "common/utils/enumTranslations";
import { ListJobsQuery, rtcJobsApi } from "common/state/rtcJobsApi";
import { NoScroll, Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { TFunction } from "i18next";
import { useMuiColorStyle } from "common/hooks/useMuiColor";
import { useNavigate } from "react-router-dom";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import NewIcon from "@mui/icons-material/LibraryAdd";
import React, { FunctionComponent, useMemo, useState } from "react";

const getRowId = (job: Job): number => {
  return job.id;
};

export type FarmsByIdType = Record<
  string,
  { farm: Farm; zonesById: Record<string, Zone> }
>;

const none = Symbol("none");
const multiple = Symbol("multiple");

/**
 * a "middleware" for rendering aggregated `multiple` values in a row group header
 *
 * @param t an i18next t() function
 * @param next a renderCell callback which handles values other than `multiple`
 * @returns a renderCell callback which renders `multiple` values directly and
 *   delegates the rest to `next`
 */
function renderMultiple<T extends GridRenderCellParams<any, any, any, any>>(
  t: TFunction,
  next: (params: T) => JSX.Element
): (params: T) => JSX.Element {
  return (params) => {
    if (params.value === multiple) {
      return <span className="italic">{t("utils.descriptors.multiple")}</span>;
    } else {
      return next(params);
    }
  };
}

const unanimous: GridAggregationFunction<string, string | symbol> = {
  apply: (params) => {
    if (params.values.length === 0) {
      return none;
    }

    const firstValue = params.values[0];

    for (let idx = 1; idx < params.values.length; idx++) {
      if (params.values[idx] !== firstValue) {
        return multiple;
      }
    }

    return firstValue;
  },
  columnTypes: ["string"],
};

/**
 * aggregation logic for job states:
 * - if all of the jobs in the group are pending, the group is pending.
 * - otherwise, if all of the non-pending jobs in the group share a common state,
 *   the group is that state.
 * - otherwise, the group has multiple states.
 */
const aggregateState: GridAggregationFunction<string, State | symbol> = {
  apply: (params) => {
    let nonPendingState: State | undefined;

    for (const jobState of params.values) {
      const parsedState = stateFromJSON(jobState);

      if (parsedState !== State.PENDING) {
        if (nonPendingState && parsedState !== nonPendingState) {
          return multiple;
        } else if (!nonPendingState) {
          nonPendingState = parsedState;
        }
      }
    }

    return nonPendingState ?? State.PENDING;
  },
};

export const JobList: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelf();
  const userCustomerId = getCustomerId(user);

  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [showDryRuns, setShowDryRuns] = useState<boolean>(false);
  const [showCreationFailures, setShowCreationFailures] =
    useState<boolean>(false);

  const [selectedRigs, setSelectedRigs] = useState<Autotractor[]>([]);
  const [selectedStates, setSelectedStates] = useState<State[]>([]);
  const [selectedFields, setSelectedFields] = useState<Zone[]>([]);

  const queryParams = useMemo(() => {
    const rigIds =
      selectedRigs.length > 0 ? selectedRigs.map((r) => r.id) : undefined;
    const states = selectedStates.length > 0 ? selectedStates : undefined;
    const fieldIds =
      selectedFields.length > 0
        ? selectedFields.flatMap((f) => f.id?.id ?? [])
        : undefined;
    const params: ListJobsQuery = {
      pageSize: 100,
      rigIds,
      states,
      fieldIds,
    };
    if (showDryRuns) {
      params.dryRuns = true;
    }
    if (showCreationFailures) {
      params.creationFailures = true;
    }
    return params;
  }, [
    showDryRuns,
    showCreationFailures,
    selectedRigs,
    selectedStates,
    selectedFields,
  ]);

  const { currentData, hasNextPage, fetchNextPage, isLoading } =
    rtcJobsApi.endpoints.listInfiniteJobs.useInfiniteQuery(queryParams);
  const allJobs: Job[] = useMemo(() => {
    return currentData ? currentData.pages.flatMap((v) => v.jobs) : [];
  }, [currentData]);
  const { data: allCustomers } = useQueryPopups(
    portalApi.endpoints.listCustomers.useQuery()
  );

  const canReadCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  const canCreateJob = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.update,
      PermissionResource.autotractor_jobs,
      PermissionDomain.customer
    )
  );

  const customersById = useMemo(() => {
    const result = new Map<number, CustomerResponse>();
    for (const customer of allCustomers ?? []) {
      const id = customer.db?.id;
      if (id !== undefined) {
        result.set(id, customer);
      }
    }
    return result;
  }, [allCustomers]);

  const filteredJobs = useMemo(() => {
    if (canReadCustomers && customerId === undefined) {
      return allJobs;
    }
    return allJobs.filter((job) => {
      if (canReadCustomers) {
        return job.customerId === customerId?.toString();
      }
      return job.customerId === userCustomerId?.toString();
    });
  }, [allJobs, customerId, canReadCustomers, userCustomerId]);

  const { data: farms } = farmApi.endpoints.listFarms.useQuery({
    contents: "no-geometry",
  });

  const farmsById = useMemo<FarmsByIdType>(() => {
    const farmsById: FarmsByIdType = {};
    if (!farms) {
      return farmsById;
    }
    for (const farm of farms) {
      if (farm.id?.id) {
        const farmAndFields: { farm: Farm; zonesById: Record<string, Zone> } = {
          farm,
          zonesById: {},
        };
        for (const zone of farm.zones) {
          if (zone.id?.id) {
            farmAndFields.zonesById[zone.id.id] = zone;
          }
        }
        farmsById[farm.id.id] = farmAndFields;
      }
    }
    return farmsById;
  }, [farms]);

  const aggregateFieldId: GridAggregationFunction<
    { farmId: string | undefined; fieldId: string | undefined },
    { farmId: string | undefined; fieldId: string | undefined } | symbol
  > = useMemo(
    () => ({
      getCellValue: ({ row }) => ({ farmId: row.farmId, fieldId: row.fieldId }),
      apply: (params) => {
        if (params.values.length === 0) {
          return none;
        }

        const firstFarmId = params.values[0]?.farmId;
        const firstFieldId = params.values[0]?.fieldId;

        for (let idx = 1; idx < params.values.length; idx++) {
          if (
            params.values[idx]?.farmId !== firstFarmId ||
            params.values[idx]?.fieldId !== firstFieldId
          ) {
            return multiple;
          }
        }

        return { farmId: firstFarmId, fieldId: firstFieldId };
      },
      valueFormatter: (
        value:
          | {
              farmId: string | undefined;
              fieldId: string | undefined;
            }
          | symbol
      ) => {
        if (typeof value === "symbol") {
          return value;
        } else if (value.farmId !== undefined && value.fieldId !== undefined) {
          // there are some transport jobs with a blank field ID; explicitly
          // checking for undefined is necessary to ensure that the formatted
          // value leaves this method as an empty string (the field ID) and not
          // an object.
          const farm = farmsById[value.farmId];
          const field = farm?.zonesById[value.fieldId];
          return field?.name ?? value.fieldId;
        }
      },
      columnTypes: ["string"],
    }),
    [farmsById]
  );

  const { data: allRigs } = atkEquipmentApi.endpoints.searchRigs.useQuery({});

  const rigsById = useMemo<{ [id: string]: Autotractor }>(() => {
    if (!allRigs) {
      return {};
    }

    const rbi: { [id: string]: Autotractor } = {};

    for (const rig of allRigs.rigs) {
      rbi[rig.id] = rig;
    }

    return rbi;
  }, [allRigs]);

  const errorColorStyle = useMuiColorStyle("error");

  const defaultColumn: Partial<GridColDef> = useMemo(
    () => ({
      disableColumnMenu: true,
      sortable: true,
      flex: 1,
    }),
    []
  );
  const columns = useMemo<GridColDef<Job>[]>(() => {
    const baseColumns: GridColDef<Job>[] = [
      {
        ...defaultColumn,
        field: "name",
        headerName: capitalize(t("utils.descriptors.name")),
      },
      {
        ...defaultColumn,
        field: "type",
        headerName: capitalize(t("models.autotractor.jobType")),
        valueFormatter: (jobType: string | undefined) => {
          return jobType
            ? capitalize(jobTypeToLocaleString(t, jobTypeFromJSON(jobType)))
            : "";
        },
      },
      {
        ...defaultColumn,
        field: "config.groundPrep.rigId",
        headerName: capitalize(t("models.equipment.rig", { count: 1 })),
        valueGetter: (_val: string, row: Job) => {
          return row.config?.groundPrep?.rigId;
        },
        renderCell: renderMultiple(
          t,
          (params) =>
            rigsById[params.value]?.customerProvidedName ?? params.value
        ),
      },
      {
        ...defaultColumn,
        field: "farmId",
        headerName: capitalize(t("models.farms.farm", { count: 1 })),
        renderCell: renderMultiple(
          t,
          (params) => farmsById[params.value]?.farm.name ?? params.value
        ),
      },
      {
        ...defaultColumn,
        field: "fieldId",
        headerName: capitalize(t("views.farms.zoneTypes.field", { count: 1 })),
        valueFormatter: (val: string, row: Job) => {
          if (!val) {
            return "";
          }
          const farm = farmsById[row.farmId];
          const field = farm?.zonesById[val];
          return field?.name ?? val;
        },
        renderCell: renderMultiple(t, (params) => {
          if (params.formattedValue === none || !params.formattedValue) {
            return "";
          } else {
            return params.formattedValue;
          }
        }),
      },
      {
        ...defaultColumn,
        field: "state",
        headerName: capitalize(
          t("models.autotractor.states.state", { count: 1 })
        ),
        valueGetter: (val: string) => stateFromJSON(val),
        renderCell: renderMultiple(t, (params) => (
          <JobStateChip state={params.value} />
        )),
      },
      {
        ...defaultColumn,
        field: "createdAt",
        type: "date",
        headerName: capitalize(t("views.reports.scheduled.table.fields.date")),
        valueGetter: (val: string) => DateTime.fromISO(val).toJSDate(),
      },
      {
        ...defaultColumn,
        field: "customerId",
        headerName: capitalize(t("models.customers.customer_one")),
        valueFormatter: (customerId: string | undefined) => {
          if (customerId === undefined) {
            return "";
          } else {
            const numId = Number.parseInt(customerId);
            return (
              customersById.get(numId)?.name ?? t("utils.descriptors.unknown")
            );
          }
        },
      },
    ];

    if (showCreationFailures) {
      baseColumns.push({
        ...defaultColumn,
        field: "creationError",
        headerName: t("utils.descriptors.error"),
        flex: 4,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <span className="truncate" style={errorColorStyle}>
              {params.value}
            </span>
          </Tooltip>
        ),
      });
    }

    return baseColumns;
  }, [
    defaultColumn,
    t,
    showCreationFailures,
    rigsById,
    farmsById,
    customersById,
    errorColorStyle,
  ]);

  const apiRef = useGridApiRef();
  const initialState: GridInitialStatePremium = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      sorting: {
        sortModel: [
          { field: "createdAt", sort: "desc" },
          { field: "state", sort: "desc" },
        ],
      },
      rowGrouping: {
        model: ["name"],
      },
      aggregation: {
        model: {
          createdAt: "max",
          type: "unanimous",
          "config.groundPrep.rigId": "unanimous",
          farmId: "unanimous",
          fieldId: "aggregateFieldId",
          state: "aggregateState",
          customerId: "unanimous",
        },
      },
    },
  });

  return (
    <>
      <Header title={capitalize(t("models.jobs.job_other"))}>
        <AtkNav />
        {canCreateJob && (
          <Button
            className="text-white ml-auto"
            startIcon={<NewIcon />}
            onClick={() => navigate(`${Path.JOBS}/create`)}
          >
            {t("views.autotractor.jobCreation.buttonLabel")}
          </Button>
        )}
      </Header>
      <Page maxWidth={false}>
        <NoScroll>
          <JobFilters
            rigs={selectedRigs}
            setRigs={setSelectedRigs}
            states={selectedStates}
            setStates={setSelectedStates}
            fields={selectedFields}
            setFields={setSelectedFields}
            farmsById={farmsById}
          />
          <CarbonDataGrid<Job>
            apiRef={apiRef}
            columns={columns}
            rows={filteredJobs}
            loading={isLoading}
            getRowId={getRowId}
            initialState={initialState}
            groupingColDef={{
              ...defaultColumn,
              flex: 2,
              hideDescendantCount: true,
            }}
            aggregationFunctions={{
              ...GRID_AGGREGATION_FUNCTIONS,
              unanimous,
              aggregateFieldId,
              aggregateState,
            }}
            getAggregationPosition={() => "inline"}
            getRowClassName={() => "cursor-pointer"}
            onRowClick={({ row: job }) =>
              job.id && navigate(getJobPath(job.id))
            }
            disableRowSelectionOnClick
            hideFooter
            header={
              <div className="flex items-center gap-4 w-full">
                {canReadCustomers && (
                  <CustomerSelector
                    small
                    value={customerId}
                    onChange={setCustomerId}
                    emptyText={t("utils.table.showAll", {
                      objects: t("models.customers.customer_other"),
                    })}
                    className="md:max-w-80"
                  />
                )}
                {canReadCustomers && (
                  <div className="flex items-center gap-4 ml-auto">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showCreationFailures}
                          onChange={(e) =>
                            setShowCreationFailures(e.target.checked)
                          }
                          color="default"
                        />
                      }
                      label={t("models.jobs.creation_failures")}
                      className="whitespace-nowrap"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showDryRuns}
                          onChange={(e) => {
                            setShowDryRuns(e.target.checked);
                          }}
                          color="default"
                        />
                      }
                      label={t("models.jobs.dry_runs")}
                      className="whitespace-nowrap"
                    />
                  </div>
                )}
              </div>
            }
            sx={{
              // don't display aggregation IDs on the columns because they're
              // hardcoded and can't be changed
              "& .MuiDataGrid-aggregationColumnHeaderLabel": {
                display: "none",
              },
            }}
          />
          {hasNextPage && (
            <Button
              variant="contained"
              className="m-2"
              onClick={() => fetchNextPage()}
            >
              {t("utils.lists.loadMore")}
            </Button>
          )}
        </NoScroll>
      </Page>
    </>
  );
};
